import React from 'react';
import get from 'lodash/get';
import gql from 'graphql-tag';
import size from 'lodash/size';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';
import { FormattedMessage } from 'react-intl';

import ImageWithFallback from '../../../../components/ImageWithFallback';
import Icon from '../../../../components/Icon';
import Header from '../../../../components/Header';
import HasRole from '../../../../components/HasRole';
import SanitizedHTML from '../../../../components/SanitizedHTML';
import PremiumHemSubscription from '../../../../components/PremiumHemSubscription';

import LockedContentComponent from './LockedContent';
import * as constants from '../../../Authorization/constants';
import { SimpleLoadingComponent } from '../../../../utils/apollo';
import '../../styles.css';
import messages from './messages';

const AboutContent = ({ data, displayImageUrl }) => (
  <div styleName="about-us-content">
    <div styleName="intro-subheader">
      <div styleName="college-image-container">
        <ImageWithFallback
          styleName="college-image"
          src={displayImageUrl}
          circular
          fallback={(
            <div>
              <Icon
                name="building outline"
                color="grey"
                size="huge"
                style={{
                  boxSizing: 'border-box',
                  border: '1px solid grey',
                  padding: '20px',
                  width: 'auto',
                  height: 'auto',
                  borderRadius: '50%',
                }}
              />
            </div>
          )}
          alt="College Logo"
        />
      </div>
      <div className="college-intro-top">
        <Header as="h3" data-cy="Premium-Hem-AboutUs-Headline">
          {data.headline || ''}
        </Header>
        <SanitizedHTML html={data.quote && `"${data.quote}"`} data-cy="Premium-Hem-AboutUs-Quote" />
      </div>
    </div>
    <div>
      <SanitizedHTML html={data.body || ''} data-cy="Premium-Hem-AboutUs-Content" />
    </div>
  </div>
);

AboutContent.propTypes = {
  data: PropTypes.object,
  displayImageUrl: PropTypes.string,
};

const IntroBody = (
  <div>With <strong>About Us</strong>, you can provide an engaging
    introduction to your institution on the Naviance college profile home
    page. This will include a photo, headline, quote and description where
    you can share with students what makes your institution unique.
  </div>
);

export class IntroComponent extends React.Component {
  static propTypes = {
    toggleUpgradeFormVisibility: PropTypes.func.isRequired,
    viewer: PropTypes.object,
    aboutUsData: PropTypes.object,
    draftImageData: PropTypes.object,
  };

  state = {
    collegeProfile: {},
  };

  componentDidMount = () => {
    const hubsSchoolProfile = get(this.props, 'viewer.hubsSchoolProfiles');
    this.setState({
      collegeProfile: hubsSchoolProfile,
    });
  }

  isNewAboutUsData = (aboutUsData) => {
    const foundData = Object.keys(aboutUsData).some((key) => aboutUsData[key] !== null && key !== 'type' && key !== '__typename');
    return !foundData;
  }

  render() {
    const { toggleUpgradeFormVisibility, aboutUsData, draftImageData } = this.props;
    const { collegeProfile } = this.state;
    const aboutContentData = size(aboutUsData) >= 3 ? aboutUsData : collegeProfile;
    const aboutImageUrl = get(draftImageData, 'scannedPhotoUrl') || get(aboutContentData, 'img');
    const isNewAboutUsData = aboutContentData ? this.isNewAboutUsData(aboutContentData) : true;

    return (
      <div styleName="intro-container">
        <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
          <PremiumHemSubscription inactive>
            <LockedContentComponent
              header="Unlock About Us"
              subheader="Captivate viewers with a detailed introduction."
              body={IntroBody}
              toggleUpgradeFormVisibility={toggleUpgradeFormVisibility}
            />
          </PremiumHemSubscription>
          <PremiumHemSubscription>
            <Header as="h1">
              Introduction
            </Header>
            <div>
              <FormattedMessage tagName="span" {...messages.introTitle} />
            </div>
            <Header as="h2">
              About us
            </Header>
            <div>
              {isNewAboutUsData ? (
                <span>
                  Provide an engaging introduction to your institution on the Naviance college profile home page.
                  Use the &quot;About us&quot; option in the menu to add a photo, headline and description.
                </span>
              ) : (
                <span>Below is a preview of what students will see on the home page of your Naviance college profile.</span>
              )}
            </div>
            {(!isNewAboutUsData || aboutImageUrl) && (
              <AboutContent
                data={aboutContentData}
                displayImageUrl={aboutImageUrl}
              />
            )}
          </PremiumHemSubscription>
        </HasRole>
      </div>
    );
  }
}

const query = gql`
  query CollegeProfileIntroPage {
    viewer {
      id
      hubsSchoolProfiles {
        img
        type
        body
        headline
        quote
        product_id
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  aboutUsData: get(state, 'nonPersistedCollegeProfile.intro.aboutUsData'),
  draftImageData: get(state, 'nonPersistedCollegeProfile.intro.draftImageData'),
});

export default compose(
  connect(mapStateToProps, {}),
  SimpleLoadingComponent(graphql(query, {
    options: { fetchPolicy: 'network-only' },
  })),
)(IntroComponent);
